<template>
    <TransitionRoot as="template" :show="true">
        <Dialog as="div" class="fixed z-50 inset-0 overflow-y-auto">
            <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <div class="relative inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-screen-xl sm:w-full sm:p-6">
                        <div>
                            <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100">
                                <CreditCardIcon class="h-6 w-6 text-gray-600" aria-hidden="true" />
                            </div>
                            <div class="mt-3 text-center sm:mt-5">
                                <DialogTitle as="h3" class="text-lg leading-6 font-medium text-gray-900">Transaction Details <span class="text-gray-400" v-if="invoice">({{ invoice.number }})</span></DialogTitle>
                                <div class="mt-4 grid sm:grid-cols-3 gap-12 p-4" v-if="invoice">

                                    <div class="col-span-2">
                                        <ul class="grid grid-cols-2 text-left font-normal text-sm">
                                            <li>
                                                <span class="text-gray-400 mr-2">Status:</span>
                                                <span v-if="hasBeenRefunded" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">refunded</span>
                                                <span v-else-if="invoice.status === 'draft'" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 bg-blue-100 text-blue-800">{{ invoice.status }}</span>
                                                <span v-else-if="invoice.status === 'open'" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{{ invoice.status }}</span>
                                                <span v-else-if="invoice.status === 'paid'" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">{{ invoice.status }}</span>
                                                <span v-else-if="invoice.status === 'uncollectible'" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{{ invoice.status }}</span>
                                                <span v-else-if="invoice.status === 'void'" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{{ invoice.status }}</span>
                                            </li>
                                            <li>
                                                <span class="text-gray-400 mr-2">Customer: </span>
                                                <router-link :to="{ name: 'users.detail', params: { userId: user.id }}"
                                                class="underline hover:text-gray-600">
                                                    <span class="truncate">{{ invoice.customer_email }}</span>
                                                </router-link>
                                            </li>
                                            <li>
                                                <span class="text-gray-400 mr-2">Due Date: </span>{{ invoice.due_date }}
                                            </li>
                                            <li>
                                                <span class="text-gray-400 mr-2">Attribution: </span>{{ invoice.metadata.attribution ?? 'Organic' }}
                                            </li>
                                        </ul>

                                        <dl class="mt-8">
                                            <div class="flex items-center justify-between" v-for="line in invoice.lines.data">
                                                <dt class="text-sm text-gray-600">
                                                    {{ line.description }}
                                                </dt>
                                                <dd class="text-sm font-medium text-gray-900 tracking-wider">{{ currencyFormat(invoice.currency, line.amount) }}</dd>
                                            </div>
                                            <div class="border-t border-gray-200 pt-4 mt-4 flex items-center justify-between">
                                                <dt class="flex text-sm text-gray-600">
                                                    <span>Subtotal</span>
                                                </dt>
                                                <dd class="text-sm font-medium text-gray-900 tracking-wider">{{ currencyFormat(invoice.currency, invoice.subtotal) }}</dd>
                                            </div>
                                            <div class="pt-2 flex items-center justify-between" v-if="invoice.discount">
                                                <dt class="flex text-sm text-gray-400">
                                                    <span class="font-normal">{{invoice.discount.coupon.name}} (
                                                    <template v-if="invoice.discount.coupon.amount_off">{{ currencyFormat(invoice.currency, invoice.discount.coupon.amount_off) }} Off</template>
                                                    <template v-else>{{ invoice.discount.coupon.percent_off }}% Off</template>
                                                    )</span>
                                                </dt>
                                                <dd class="text-sm font-medium text-gray-600 tracking-wider">-{{ currencyFormat(invoice.currency, invoice.total_discount_amounts[0].amount)}}</dd>
                                            </div>
                                            <div class="pt-2 flex items-center justify-between">
                                                <dt class="flex text-sm text-gray-400">
                                                    Tax&nbsp;<span class="font-normal">(<template v-for="(rate, index) in invoice.default_tax_rates"><template v-if="rate.active">{{rate.display_name}} {{ rate.percentage }}%</template><template v-if="index < (invoice.default_tax_rates.length - 1)">,&nbsp;</template></template>)</span>
                                                </dt>
                                                <dd class="text-sm font-medium text-gray-600 tracking-wider">{{ currencyFormat(invoice.currency, invoice.tax) }}</dd>
                                            </div>
                                            <div class="border-t border-gray-200 pt-4 mt-4 flex items-center justify-between">
                                                <dt class="text-base font-medium text-gray-900">Order total</dt>
                                                <dd class="text-base font-medium text-gray-900 tracking-wider">{{ currencyFormat(invoice.currency, invoice.total) }}</dd>
                                            </div>
                                            <div class="pt-2 flex items-center justify-between">
                                                <dt class="text-sm text-gray-400">Total Paid</dt>
                                                <dd class="text-sm font-medium text-gray-600 tracking-wider">{{ currencyFormat(invoice.currency, invoice.amount_paid) }}</dd>
                                            </div>
                                            <div class="pt-2 flex items-center justify-between">
                                                <dt class="text-sm text-gray-400">Total Remaining</dt>
                                                <dd class="text-sm font-medium text-gray-600 tracking-wider">{{ currencyFormat(invoice.currency, invoice.amount_due) }}</dd>
                                            </div>
                                        </dl>
                                    </div>
                                    <div class="col-span-1">
                                        <div class="flow-root">
                                            <ul role="list" class="-mb-8">
                                                <li>
                                                    <div class="relative pb-8">
                                                        <span class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                                                        <div class="relative flex space-x-3">
                                                            <div>
                                                              <span class="bg-purple-400 h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white">
                                                                  <SparklesIcon class="h-5 w-5 text-white" aria-hidden="true" />
                                                              </span>
                                                            </div>
                                                            <div class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                                                <div>
                                                                    <p class="text-sm text-gray-500">
                                                                        <time :datetime="invoice.created * 1000">{{ formatDateShort(invoice.created * 1000) }}</time>
                                                                    </p>
                                                                </div>
                                                                <div class="text-right text-sm whitespace-nowrap text-gray-400">
                                                                    Created
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                                <li>
                                                    <div class="relative pb-8">
                                                        <span class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                                                        <div class="relative flex space-x-3">
                                                            <div>
                                                                <span class="bg-blue-400 h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white">
                                                                    <LockClosedIcon class="h-5 w-5 text-white" aria-hidden="true" />
                                                                </span>
                                                            </div>
                                                            <div class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                                                <div>
                                                                    <p class="text-sm text-gray-500">
                                                                        <time :datetime="invoice.status_transitions.finalized_at * 1000">{{ formatDateShort(invoice.status_transitions.finalized_at * 1000) }}</time>
                                                                    </p>
                                                                </div>
                                                                <div class="text-right text-sm whitespace-nowrap text-gray-400">
                                                                    Finalised
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                                <li v-if="!invoice.status_transitions.paid_at">
                                                    <div class="relative pb-8">
                                                        <span class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" v-if="invoice.status_transitions.voided_at || invoice.status_transitions.marked_uncollectible_at" />
                                                        <div class="relative flex space-x-3">
                                                            <div>
                                                                <span class="bg-gray-400 h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white">
                                                                    <BanknotesIcon class="h-5 w-5 text-white" aria-hidden="true" />
                                                                </span>
                                                            </div>
                                                            <div class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                                                <div>
                                                                    <p class="text-sm text-gray-500">
                                                                        Not yet paid
                                                                    </p>
                                                                </div>
                                                                <div class="text-right text-sm whitespace-nowrap text-gray-400">
                                                                    Paid
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                                <li v-if="invoice.status_transitions.paid_at">
                                                    <div class="relative pb-8">
                                                        <span class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" v-if="invoice.status_transitions.voided_at || invoice.status_transitions.marked_uncollectible_at" />
                                                        <div class="relative flex space-x-3">
                                                            <div>
                                                                <span class="bg-green-400 h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white">
                                                                    <BanknotesIcon class="h-5 w-5 text-white" aria-hidden="true" />
                                                                </span>
                                                            </div>
                                                            <div class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                                                <div>
                                                                    <p class="text-sm text-gray-500">
                                                                        <time :datetime="invoice.status_transitions.paid_at * 1000">{{ formatDateShort(invoice.status_transitions.paid_at * 1000) }}</time>
                                                                    </p>
                                                                </div>
                                                                <div class="text-right text-sm whitespace-nowrap text-gray-400">
                                                                    Paid
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>

                                                <li v-if="invoice.status_transitions.voided_at">
                                                    <div class="relative pb-8">
                                                        <span class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" v-if="invoice.status_transitions.marked_uncollectible_at" />
                                                        <div class="relative flex space-x-3">
                                                            <div>
                                                                <span class="bg-red-400 h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white">
                                                                    <NoSymbolIcon class="h-5 w-5 text-white" aria-hidden="true" />
                                                                </span>
                                                            </div>
                                                            <div class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                                                <div>
                                                                    <p class="text-sm text-gray-500">
                                                                        <time :datetime="invoice.status_transitions.voided_at * 1000">{{ formatDateShort(invoice.status_transitions.voided_at * 1000) }}</time>
                                                                    </p>
                                                                </div>
                                                                <div class="text-right text-sm whitespace-nowrap text-gray-400">
                                                                    Voided
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>

                                                <li v-if="invoice.status_transitions.marked_uncollectible_at">
                                                    <div class="relative pb-8">
                                                        <div class="relative flex space-x-3">
                                                            <div>
                                                                <span class="bg-gray-400 h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white">
                                                                    <ClockIcon class="h-5 w-5 text-white" aria-hidden="true" />
                                                                </span>
                                                            </div>
                                                            <div class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                                                <div>
                                                                    <p class="text-sm text-gray-500">
                                                                        <time :datetime="invoice.status_transitions.marked_uncollectible_at * 1000">{{ formatDateShort(invoice.status_transitions.marked_uncollectible_at * 1000) }}</time>
                                                                    </p>
                                                                </div>
                                                                <div class="text-right text-sm whitespace-nowrap text-gray-400">
                                                                    Marked Uncollectible
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex justify-between mt-8" v-if="invoice">
                                    <div class="flex">
                                        <button type="button" v-if="invoice.status === 'paid' && !hasBeenRefunded" @click.prevent="validateRefund" class="ml-3 inline-flex justify-center py-2 px-4 border shadow-sm text-sm font-medium rounded-md border-red-600 text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600">
                                            <ArrowPathIcon class="h-5 w-5 text-white animate-spin mr-2" v-if="processingRefund" />
                                            Refund Transaction
                                        </button>
                                        <button type="button" @click.prevent="viewHostedInvoice" class="ml-3 inline-flex justify-center py-2 px-4 border shadow-sm text-sm font-medium rounded-md border-gray-300 text-gray-700 bg-white hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                            View Hosted Invoice
                                        </button>
                                        <button type="button" @click.prevent="viewOnStripe" class="ml-3 inline-flex justify-center py-2 px-4 border shadow-sm text-sm font-medium rounded-md border-gray-300 text-gray-700 bg-white hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                            View on Stripe
                                        </button>
                                    </div>
                                    <div class="flex">
                                        <button type="button" @click="goBack" class="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import {useRoute, useRouter} from "vue-router"
import {inject, onMounted, ref} from "vue"
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from "@headlessui/vue"
import {
    NoSymbolIcon,
    BanknotesIcon,
    ClockIcon,
    CreditCardIcon,
    LockClosedIcon,
    ArrowPathIcon,
    SparklesIcon
} from "@heroicons/vue/24/outline"
import {useBus} from "../../../app";

export default {
    name: "View",
    components: {
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        CreditCardIcon,
        SparklesIcon,
        LockClosedIcon,
        NoSymbolIcon,
        BanknotesIcon,
        ClockIcon,
        ArrowPathIcon
    },
    setup() {
        const alert = inject('alert')
        const bus = useBus()
        const vueRouter = useRouter()
        const vueRoute = useRoute()
        const invoiceId = vueRoute.params.invoice
        const invoice = ref(null)
        const user = ref(null)
        const processingRefund = ref(false)
        const hasBeenRefunded = ref(false)

        const getInvoiceData = async () => {
            await axios.get(route('transactions.show', {invoiceId: invoiceId}))
                .then((response) => {
                    invoice.value = response.data.invoice
                    user.value = response.data.user

                    if (invoice.value.payment_intent?.charges?.data[0]?.amount_refunded > 0) {
                        hasBeenRefunded.value = true
                    }
                })
        }

        const goBack = () => {
            vueRouter.back()
        }

        const viewHostedInvoice = () => {
            window.open(invoice.value.hosted_invoice_url, '_blank')
        }

        const viewOnStripe = () => {
            window.open('https://dashboard.stripe.com/invoices/' + invoice.value.id, '_blank')
        }

        const processRefund = async (payment, subscription = null) => {
            processingRefund.value = true
            await axios.post(route('transactions.refund'), {payment: payment.id, subscription: subscription})
                .then((response) => {
                    bus.$emit('alert.success', 'Refund Successful')
                    getInvoiceData()
                })
                .catch((e) => {
                    bus.$emit('alert.danger', e.message)
                    processingRefund.value = false
                })
        }

        const validateRefund = async () => {
            if (invoice.value.subscription) {
                await alert.fire({
                    title: 'This sale is part of a subscription.',
                    text: 'Do you also want the subscription to be cancelled?',
                    icon: 'warning',
                    showCancelButton: true,
                    showDenyButton: true,
                    confirmButtonText: 'Refund & cancel',
                    denyButtonText: 'Just refund',
                    cancelButtonText: 'Go back'
                })
                    .then((selected) => {
                        if (selected.isConfirmed) {
                            processRefund(invoice.value.payment_intent, invoice.value.subscription)
                        } else if (selected.isDenied) {
                            processRefund(invoice.value.payment_intent)
                        }
                    })
            } else {
                await alert.fire({
                    title: 'Refund request',
                    text: 'Are you sure you want to refund this transaction?',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, refund the sale',
                    cancelButtonText: 'No, go back'
                })
                    .then((selected) => {
                        if (selected.isConfirmed) {
                            processRefund(invoice.value.payment_intent)
                        }
                    })
            }
        }

        onMounted(() => {
            getInvoiceData()
        })

        return {
            goBack,
            user,
            invoice,
            invoiceId,
            getInvoiceData,
            viewHostedInvoice,
            viewOnStripe,
            validateRefund,
            processingRefund,
            hasBeenRefunded,
        }
    }
}
</script>

<style scoped>

</style>
